/**
 * Public display starting prices for ARO, AEO, and CRO.
 *
 * These are marketing starting prices — not Stripe checkout amounts and not
 * a fixed subscription total. Do not change `aiEmployeePlanPriceCents`,
 * CHANNEL_MONTHLY_CENTS, or live Stripe products from this file.
 */

import { SITEGLUE_MARKETING_ORIGIN } from "~/lib/auth-paths";
import {
  AUDIT_AEO_CTA,
  AUDIT_AEO_HEADLINE,
  AUDIT_AEO_HREF,
  AUDIT_ARO_CTA,
  AUDIT_ARO_HEADLINE,
  AUDIT_ARO_HREF,
  AUDIT_CRO_CTA,
  AUDIT_CRO_HEADLINE,
  AUDIT_CRO_HREF,
} from "~/lib/siteglue/landing-redesign/copy";
import { formatUsdFromCents } from "~/lib/siteglue/pricing-configurator";

export type PublicPricingServiceId = "aro" | "aeo" | "cro";

export const PUBLIC_SERVICE_STARTING_CENTS = {
  aro: 9900,
  aeo: 14900,
  cro: 4900,
} as const;

export function formatPublicStartingPrice(id: PublicPricingServiceId): string {
  return `From ${formatUsdFromCents(PUBLIC_SERVICE_STARTING_CENTS[id])}/month`;
}

export type PublicPricingCard = {
  id: PublicPricingServiceId;
  eyebrow: string;
  title: string;
  fullName: string;
  headline: string;
  priceLabel: string;
  startingCents: number;
  href: string;
  ctaLabel: string;
  auditHeadline: string;
  planned: boolean;
  features: readonly string[];
};

export const PUBLIC_PRICING_CARDS: readonly PublicPricingCard[] = [
  {
    id: "aro",
    eyebrow: "ARO",
    title: "Agent Readiness Optimization",
    fullName: "ARO — Agent Readiness Optimization",
    headline: "Help AI agents discover, understand, and interact with your business.",
    priceLabel: formatPublicStartingPrice("aro"),
    startingCents: PUBLIC_SERVICE_STARTING_CENTS.aro,
    href: AUDIT_ARO_HREF,
    ctaLabel: AUDIT_ARO_CTA,
    auditHeadline: AUDIT_ARO_HEADLINE,
    planned: false,
    features: [
      "ARO audit and benchmark reporting",
      "Prioritized recommendations",
      "WordPress plugin for supported ARO artifacts",
      "Rescan and verification workflow",
      "SiteGlue Agent channels configured after your audit",
    ],
  },
  {
    id: "aeo",
    eyebrow: "AEO",
    title: "Answer Engine Optimization",
    fullName: "AEO — Answer Engine Optimization",
    headline: "Improve how AI answer engines discover, cite, and recommend your business.",
    priceLabel: formatPublicStartingPrice("aeo"),
    startingCents: PUBLIC_SERVICE_STARTING_CENTS.aeo,
    href: AUDIT_AEO_HREF,
    ctaLabel: AUDIT_AEO_CTA,
    auditHeadline: AUDIT_AEO_HEADLINE,
    planned: true,
    features: [
      "AI visibility auditing (live tests when available)",
      "Brand mention and citation analysis (planned until live tests run)",
      "Competitive comparisons (planned)",
      "Answer accuracy (planned)",
      "Optimization recommendations",
    ],
  },
  {
    id: "cro",
    eyebrow: "CRO",
    title: "Conversion Rate Optimization",
    fullName: "CRO — Conversion Rate Optimization",
    headline: "Improve website conversions and customer interactions across SiteGlue Agent channels.",
    priceLabel: formatPublicStartingPrice("cro"),
    startingCents: PUBLIC_SERVICE_STARTING_CENTS.cro,
    href: AUDIT_CRO_HREF,
    ctaLabel: AUDIT_CRO_CTA,
    auditHeadline: AUDIT_CRO_HEADLINE,
    planned: false,
    features: [
      "CRO audit and benchmark reporting",
      "Conversion flow across website and SiteGlue Agent channels",
      "Prioritized recommendations",
      "Rescan and verification workflow",
      "SiteGlue Agent channels configured after your audit",
    ],
  },
];

export const PUBLIC_PRICING_INTRO =
  "One product: SiteGlue Agent. Three optimization services: AEO, ARO, and CRO. Run a free audit first — then activate the service that matches your results.";

export const PUBLIC_PRICING_STARTING_NOTE =
  "These are starting prices, not a fixed subscription total. SiteGlue Agent channels are configured after your audit. Existing subscriptions are unchanged.";

export const PUBLIC_PRICING_FLOW =
  "Run a free audit, review your results, sign up, then request activation.";

export function publicPricingCard(
  id: PublicPricingServiceId,
): PublicPricingCard {
  const card = PUBLIC_PRICING_CARDS.find((item) => item.id === id);
  if (!card) {
    throw new Error(`Unknown public pricing service: ${id}`);
  }
  return card;
}

export function publicPricingFaq(id: PublicPricingServiceId): { q: string; a: string } {
  const card = publicPricingCard(id);
  return {
    q: `How much does ${card.eyebrow} cost?`,
    a: `${card.eyebrow} starts at ${card.priceLabel} after you run a free audit, review your results, and activate. That is a starting price, not a fixed subscription total. SiteGlue Agent channels stay configurable during post-audit onboarding.`,
  };
}

export function publicPricingJsonLd(origin: string = SITEGLUE_MARKETING_ORIGIN) {
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "SiteGlue Optimization Services",
    description: PUBLIC_PRICING_INTRO,
    url: `${origin}/pricing`,
    itemListElement: PUBLIC_PRICING_CARDS.map((card, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Offer",
        name: card.fullName,
        description: card.headline,
        url: `${origin}${card.href}`,
        price: String(card.startingCents / 100),
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: String(card.startingCents / 100),
          priceCurrency: "USD",
          unitText: "MONTH",
          name: card.priceLabel,
        },
      },
    })),
  };
}
